import { useMemo } from 'react'
import { Box } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import type { BudgetSummaryProps } from '@/modules/budget/components/BudgetSummary/BudgetSummary.types'

import { AddBudget } from '@/components/AddBudget/AddBudget'
import { BudgetsList } from '@/components/BudgetsList/BudgetsList'
import { BudgetSpendSummary } from '@/components/BudgetSpendSummary/BudgetSpendSummary'
import { Loader } from '@/components/Loader/Loader'
import { Title } from '@/components/Title/Title'

import * as Common from '@/__generated__/queries/common'
import { AdvisorsService, BudgetService } from '@/__generated__/requests'
import type { BudgetStats } from '@/__generated__/requests/types.gen'

import classes from './BudgetSummary.module.css'

export function BudgetSummary({
  endDate,
  selectedType,
  startDate,
  userId,
}: BudgetSummaryProps) {
  const isAdvisor = !!userId

  const queryFn = isAdvisor
    ? () =>
        AdvisorsService.advisorUsersControllerGetBudgets({
          endDate,
          startDate,
          userId,
        })
    : () =>
        BudgetService.budgetControllerGetAllBudgets({
          endDate,
          startDate,
        })

  const queryKey = isAdvisor
    ? Common.UseAdvisorsServiceAdvisorUsersControllerGetBudgetsKeyFn({
        endDate,
        startDate,
        userId,
      })
    : Common.UseBudgetServiceBudgetControllerGetAllBudgetsKeyFn({
        endDate,
        startDate,
      })

  const { data, isFetching, isLoading } = useQuery({
    queryFn,
    queryKey,
  })

  const budgets = useMemo<BudgetStats | null>(() => {
    if (!data) {
      return null
    }

    return data
  }, [data])

  return (
    <Box className={classes.container}>
      <Title variant="h1">Budget</Title>

      {isFetching || isLoading ? (
        <Box h={110}>
          <Loader isMinimized />
        </Box>
      ) : (
        <>
          <BudgetSpendSummary
            budgets={budgets}
            month={dayjs(startDate).format('MMMM')}
            readOnly={isAdvisor}
            selectedType={selectedType}
          />

          {!isAdvisor && (
            <AddBudget currentBudget={budgets?.totalBudget ?? 0} />
          )}

          <BudgetsList
            budgets={budgets?.budgets}
            currentBudget={budgets?.totalBudget ?? 0}
            readOnly={isAdvisor}
          />
        </>
      )}
    </Box>
  )
}
